/**
 * Рисование инструментов на основе объёма (счёт — в volume-tools.ts).
 *
 * Свой облик, а не калька: линия VWAP светится, полосы переливаются от
 * линии к краю; столбцы профиля темнее у основания и ярче к вершине,
 * зона стоимости яркая, остальное приглушено; точка контроля — светящаяся
 * линия с ценой в пилюле.
 *
 * Счёт повторяется на каждый кадр прокрутки, поэтому результат помнится,
 * пока не изменились свечи, точки или настройки.
 */

import { withAlpha } from './color.js';
import type { DrawScope, Rect, Screen } from './shapes.js';
import type { Drawing } from './types.js';
import {
  anchoredVwap,
  bandOffset,
  rowSizeOf,
  volumeOptionsOf,
  volumeProfile,
  volumeText,
  type VolumeOptions,
  type VolumeProfile,
  type VwapPoint,
} from './volume-tools.js';

/** По чему фигура ловится курсором: линии и площадь гистограммы — то, что нарисовано. */
export interface VolumeHit {
  readonly lines: ReadonlyArray<readonly Screen[]>;
  readonly rects: readonly Rect[];
  /** Рамка диапазона: ловится краем, а не всей площадью — под ней свечи и чужие фигуры. */
  readonly frame: Rect | null;
}

const hits = new Map<string, VolumeHit>();

export function volumeHitOf(id: string): VolumeHit | null {
  return hits.get(id) ?? null;
}

const cache = new Map<string, { key: string; value: unknown }>();

function remembered<T>(scope: DrawScope, shape: Drawing, options: VolumeOptions, make: () => T): T {
  const last = scope.candles[scope.candles.length - 1];
  const key = [
    shape.kind,
    scope.candles.length,
    scope.candles[0]?.time,
    last?.time,
    last?.close,
    last?.volume,
    scope.tickSize,
    shape.points.map((point) => point.time).join(','),
    options.source,
    options.rowsMode,
    options.rowSize,
    options.valueArea,
    options.mode,
  ].join('|');
  const known = cache.get(shape.id);
  if (known && known.key === key) return known.value as T;
  const value = make();
  cache.set(shape.id, { key, value });
  // Превью и черновики приходят с разовыми идентификаторами: память не должна расти без конца.
  if (cache.size > 200) cache.delete(cache.keys().next().value!);
  return value;
}

function priceText(value: number): string {
  const abs = Math.abs(value);
  const digits = abs >= 1000 ? 1 : abs >= 1 ? 2 : abs >= 0.01 ? 4 : 8;
  return value.toFixed(digits);
}

/** Подпись пилюлей: тёмное стекло, кромка цвета фигуры. `align` — куда пилюля растёт от точки. */
function pill(scope: DrawScope, at: Screen, text: string, color: string, align: 'left' | 'right' = 'left'): void {
  const { ctx } = scope;
  ctx.save();
  ctx.setLineDash([]);
  ctx.shadowBlur = 0;
  ctx.font = '600 11px Onest, system-ui, sans-serif';
  const width = ctx.measureText(text).width + 14;
  const height = 18;
  const wanted = align === 'left' ? at.x + 6 : at.x - 6 - width;
  const left = Math.max(2, Math.min(scope.width - width - 2, wanted));
  const top = Math.max(2, Math.min(scope.height - height - 2, at.y - height / 2));
  ctx.beginPath();
  ctx.roundRect(left, top, width, height, height / 2);
  ctx.fillStyle = 'rgba(7, 11, 22, 0.9)';
  ctx.fill();
  ctx.strokeStyle = withAlpha(color, 0.75);
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, left + 7, top + height / 2 + 0.5);
  ctx.restore();
}

function glowLine(ctx: CanvasRenderingContext2D, points: readonly Screen[], color: string, width: number, glow: number): void {
  if (points.length < 2) return;
  ctx.save();
  ctx.setLineDash([]);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.shadowColor = color;
  ctx.shadowBlur = glow;
  ctx.beginPath();
  ctx.moveTo(points[0]!.x, points[0]!.y);
  for (let i = 1; i < points.length; i += 1) ctx.lineTo(points[i]!.x, points[i]!.y);
  ctx.stroke();
  ctx.restore();
}

/** Вертикаль привязки: откуда инструмент считает. */
function anchorMark(scope: DrawScope, x: number, color: string): void {
  const { ctx } = scope;
  ctx.save();
  ctx.setLineDash([3, 5]);
  ctx.shadowBlur = 0;
  ctx.strokeStyle = withAlpha(color, 0.28);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, 0);
  ctx.lineTo(x, scope.height);
  ctx.stroke();
  ctx.restore();
}

/* ------------------------------------------------------------ VWAP с привязкой */

export function drawAnchoredVwap(scope: DrawScope, shape: Drawing, a: Screen): void {
  const { ctx } = scope;
  const options = volumeOptionsOf(shape.kind, shape.volume);
  const anchor = shape.points[0]?.time ?? 0;
  const series = remembered(scope, shape, options, () => anchoredVwap(scope.candles, anchor, options.source));
  if (series.length === 0) {
    // Привязка правее последней свечи или свечей ещё нет — только отметка места.
    anchorMark(scope, a.x, shape.color);
    scope.anchors?.set(shape.id, [a]);
    hits.set(shape.id, { lines: [], rects: [], frame: null });
    return;
  }

  const path = (price: (point: VwapPoint) => number): Screen[] => {
    const out: Screen[] = [];
    for (const point of series) {
      const at = scope.screen({ time: point.time, price: price(point) });
      // За левым и правым краем достаточно по одной точке: линия уходит за край, а не обрывается.
      if (!at) continue;
      if (at.x < -40 && out.length > 0 && out[out.length - 1]!.x < -40) out[out.length - 1] = at;
      else out.push(at);
      if (at.x > scope.width + 40) break;
    }
    return out;
  };

  const main = path((point) => point.value);
  const first = main[0];
  if (!first) return;
  anchorMark(scope, first.x, shape.color);

  // Полосы — от дальней к ближней: ближняя заливка ложится поверх и выходит гуще.
  const bands = options.bands.map((band, index) => ({ ...band, index })).filter((band) => band.on && band.multiplier > 0);
  for (const band of [...bands].reverse()) {
    const upper = path((point) => point.value + bandOffset(point, options.bandMode, band.multiplier));
    const lower = path((point) => point.value - bandOffset(point, options.bandMode, band.multiplier));
    if (upper.length < 2 || lower.length < 2) continue;
    if (options.bandFill) {
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(upper[0]!.x, upper[0]!.y);
      for (const point of upper) ctx.lineTo(point.x, point.y);
      for (let i = lower.length - 1; i >= 0; i -= 1) ctx.lineTo(lower[i]!.x, lower[i]!.y);
      ctx.closePath();
      ctx.fillStyle = withAlpha(options.bandColor, 0.05);
      ctx.fill();
      ctx.restore();
    }
    const alpha = 0.85 - band.index * 0.2;
    glowLine(ctx, upper, withAlpha(options.bandColor, alpha), 1.1, 0);
    glowLine(ctx, lower, withAlpha(options.bandColor, alpha), 1.1, 0);
    const end = upper[upper.length - 1]!;
    if (scope.hoveredId === shape.id || scope.activeId === shape.id) {
      const unit = options.bandMode === 'percent' ? '%' : 'σ';
      pill(scope, { x: Math.min(end.x, scope.width - 4), y: end.y }, `+${String(band.multiplier).replace('.', ',')} ${unit}`, options.bandColor, 'right');
    }
  }

  glowLine(ctx, main, shape.color, Math.max(1.6, shape.width + 0.6), 10);

  // Начало линии — кружком: видно, с какой свечи идёт счёт.
  ctx.save();
  ctx.setLineDash([]);
  ctx.shadowColor = shape.color;
  ctx.shadowBlur = 8;
  ctx.fillStyle = '#070b16';
  ctx.strokeStyle = shape.color;
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.arc(first.x, first.y, 3.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.restore();

  const last = series[series.length - 1]!;
  const end = main[main.length - 1]!;
  if (options.priceTag && end.x >= 0 && end.x <= scope.width + 40) {
    // У правого края пилюле некуда расти вправо: она встаёт над концом линии, а не на неё.
    const tight = end.x > scope.width - 120;
    pill(scope, { x: Math.min(end.x, scope.width - 4), y: tight ? end.y - 16 : end.y }, `VWAP ${priceText(last.value)}`, shape.color, tight ? 'right' : 'left');
  }

  scope.anchors?.set(shape.id, [first]);
  hits.set(shape.id, { lines: [main], rects: [], frame: null });
}

/* ------------------------------------------------------------ профили объёма */

interface ProfileFrame {
  /** Диапазон по времени на экране. */
  readonly left: number;
  readonly right: number;
  /** Откуда растут столбцы и в какую сторону. */
  readonly base: number;
  readonly direction: 1 | -1;
  readonly reach: number;
  /** Докуда тянутся уровни. */
  readonly levelsTo: number;
}

function drawProfile(scope: DrawScope, shape: Drawing, profile: VolumeProfile, options: VolumeOptions, frame: ProfileFrame): { poc: number; top: number; bottom: number } | null {
  const { ctx } = scope;
  const yOf = (price: number): number | null => scope.screen({ time: profile.firstTime, price })?.y ?? null;
  const top = yOf(profile.high);
  const bottom = yOf(profile.low);
  if (top === null || bottom === null) return null;

  const lit = scope.hoveredId === shape.id || scope.activeId === shape.id;

  if (options.box) {
    ctx.save();
    ctx.setLineDash([]);
    ctx.shadowBlur = 0;
    const wash = ctx.createLinearGradient(frame.left, 0, frame.right, 0);
    wash.addColorStop(0, withAlpha(shape.color, frame.direction === 1 ? 0.09 : 0.03));
    wash.addColorStop(1, withAlpha(shape.color, frame.direction === 1 ? 0.03 : 0.09));
    ctx.fillStyle = wash;
    ctx.fillRect(frame.left, top, frame.right - frame.left, bottom - top);
    ctx.setLineDash([4, 5]);
    ctx.strokeStyle = withAlpha(shape.color, lit ? 0.6 : 0.3);
    ctx.lineWidth = 1;
    ctx.strokeRect(frame.left + 0.5, top + 0.5, frame.right - frame.left - 1, bottom - top - 1);
    ctx.restore();
  }

  // Перелив вдоль столбца — один на цвет и яркость, а не на каждую строку.
  const fills = new Map<string, CanvasGradient>();
  const fillOf = (color: string, strong: boolean): CanvasGradient => {
    const key = `${color}|${strong}`;
    let fill = fills.get(key);
    if (!fill) {
      fill = ctx.createLinearGradient(frame.base, 0, frame.base + frame.direction * frame.reach, 0);
      // Столбцы лежат поверх свечей: у основания почти прозрачны, чтобы цена под ними читалась.
      fill.addColorStop(0, withAlpha(color, strong ? 0.26 : 0.08));
      fill.addColorStop(1, withAlpha(color, strong ? 0.82 : 0.3));
      fills.set(key, fill);
    }
    return fill;
  };

  const peak = profile.peak > 0 ? profile.peak : 1;
  const bar = (from: number, length: number, y: number, height: number, color: string, strong: boolean): void => {
    if (length < 0.5) return;
    const x = frame.direction === 1 ? frame.base + from : frame.base - from - length;
    ctx.fillStyle = fillOf(color, strong);
    ctx.beginPath();
    ctx.roundRect(x, y, length, height, Math.min(2, height / 2));
    ctx.fill();
  };

  ctx.save();
  ctx.setLineDash([]);
  ctx.shadowBlur = 0;
  ctx.font = '500 10px Onest, system-ui, sans-serif';
  ctx.textBaseline = 'middle';
  profile.rows.forEach((row, index) => {
    const y1 = yOf(row.to);
    const y2 = yOf(row.from);
    if (y1 === null || y2 === null) return;
    const gap = y2 - y1 >= 4 ? 1 : 0;
    const height = Math.max(1, y2 - y1 - gap);
    const strong = index >= profile.valueLow && index <= profile.valueHigh;
    let length = 0;
    if (options.mode === 'updown') {
      const up = (row.up / peak) * frame.reach;
      const down = (row.down / peak) * frame.reach;
      bar(0, up, y1, height, options.upColor, strong);
      bar(up, down, y1, height, options.downColor, strong);
      length = up + down;
    } else if (options.mode === 'delta') {
      length = (Math.abs(row.up - row.down) / peak) * frame.reach;
      bar(0, length, y1, height, row.up >= row.down ? options.upColor : options.downColor, strong);
    } else {
      length = ((row.up + row.down) / peak) * frame.reach;
      bar(0, length, y1, height, shape.color, strong);
    }
    if (options.values && height >= 10 && length > 0) {
      const value = options.mode === 'delta' ? row.up - row.down : row.up + row.down;
      ctx.fillStyle = withAlpha('#EEF3FA', strong ? 0.9 : 0.6);
      ctx.textAlign = frame.direction === 1 ? 'left' : 'right';
      ctx.fillText(volumeText(value), frame.base + frame.direction * (length + 5), y1 + height / 2 + 0.5);
    }
  });
  ctx.restore();

  // Границы зоны стоимости — тонким пунктиром, с подписями у начала.
  if (options.valueLines) {
    const high = yOf(profile.rows[profile.valueHigh]!.to);
    const low = yOf(profile.rows[profile.valueLow]!.from);
    ctx.save();
    ctx.setLineDash([5, 4]);
    ctx.shadowBlur = 0;
    ctx.lineWidth = 1;
    for (const [y, color] of [[high, options.upColor], [low, options.downColor]] as const) {
      if (y === null) continue;
      ctx.strokeStyle = withAlpha(color, 0.7);
      ctx.beginPath();
      ctx.moveTo(frame.left, y);
      ctx.lineTo(frame.levelsTo, y);
      ctx.stroke();
    }
    ctx.restore();
    if (lit) {
      if (high !== null) pill(scope, { x: frame.left, y: high }, `VAH ${priceText(profile.rows[profile.valueHigh]!.to)}`, options.upColor, 'right');
      if (low !== null) pill(scope, { x: frame.left, y: low }, `VAL ${priceText(profile.rows[profile.valueLow]!.from)}`, options.downColor, 'right');
    }
  }

  const pocRow = profile.rows[profile.poc]!;
  const pocPrice = (pocRow.from + pocRow.to) / 2;
  const poc = yOf(pocPrice) ?? (top + bottom) / 2;
  if (options.poc) {
    glowLine(ctx, [{ x: frame.left, y: poc }, { x: frame.levelsTo, y: poc }], options.pocColor, 1.6, 8);
    pill(scope, { x: Math.min(frame.levelsTo, scope.width - 4), y: poc }, `POC ${priceText(pocPrice)}`, options.pocColor, frame.levelsTo > scope.width - 110 ? 'right' : 'left');
  }

  if (lit) {
    pill(scope, { x: frame.left, y: top - 12 }, `Объём ${volumeText(profile.total)} · свечей ${profile.bars}`, shape.color, 'left');
  }
  return { poc, top, bottom };
}

/** Нет свечей в диапазоне — показать хотя бы, где он: иначе фигура пропадает из виду. */
function emptyRange(scope: DrawScope, shape: Drawing, points: readonly Screen[]): void {
  for (const point of points) anchorMark(scope, point.x, shape.color);
  scope.anchors?.set(shape.id, [...points]);
  hits.set(shape.id, { lines: points.map((point) => [{ x: point.x, y: 0 }, { x: point.x, y: scope.height }]), rects: [], frame: null });
}

function histogramRect(frame: ProfileFrame, top: number, bottom: number): Rect {
  const tip = frame.base + frame.direction * frame.reach;
  return { left: Math.min(frame.base, tip), right: Math.max(frame.base, tip), top, bottom };
}

export function drawRangeProfile(scope: DrawScope, shape: Drawing, a: Screen, b: Screen): void {
  const options = volumeOptionsOf(shape.kind, shape.volume);
  const from = shape.points[0]?.time ?? 0;
  const to = shape.points[1]?.time ?? from;
  const profile = remembered(scope, shape, options, () => volumeProfile(scope.candles, from, to, options, scope.tickSize));
  if (!profile) {
    emptyRange(scope, shape, [a, b]);
    return;
  }
  const xOf = (time: number): number | null => scope.screen({ time, price: profile.low })?.x ?? null;
  const first = xOf(profile.firstTime);
  const lastX = xOf(profile.lastTime);
  if (first === null || lastX === null) return;
  const left = Math.min(first, lastX);
  // Диапазон из одной свечи всё равно должен быть виден и хватаем.
  const right = Math.max(first, lastX, left + 24);
  const reach = Math.max(36, ((right - left) * options.width) / 100);
  const frame: ProfileFrame = {
    left,
    right,
    base: options.side === 'left' ? left : right,
    direction: options.side === 'left' ? 1 : -1,
    reach,
    levelsTo: options.extendRight ? scope.width : right,
  };
  const drawn = drawProfile(scope, shape, profile, options, frame);
  if (!drawn) return;
  const middle = (drawn.top + drawn.bottom) / 2;
  const start = { x: left, y: middle };
  const finish = { x: right, y: middle };
  // Опоры — на краях диапазона, в порядке точек фигуры.
  scope.anchors?.set(shape.id, from <= to ? [start, finish] : [finish, start]);
  hits.set(shape.id, {
    lines: options.poc ? [[{ x: left, y: drawn.poc }, { x: frame.levelsTo, y: drawn.poc }]] : [],
    rects: [histogramRect(frame, drawn.top, drawn.bottom)],
    frame: { left, right, top: drawn.top, bottom: drawn.bottom },
  });
}

export function drawAnchoredProfile(scope: DrawScope, shape: Drawing, a: Screen): void {
  const options = volumeOptionsOf(shape.kind, shape.volume);
  const anchor = shape.points[0]?.time ?? 0;
  const profile = remembered(scope, shape, options, () => volumeProfile(scope.candles, anchor, Number.POSITIVE_INFINITY, options, scope.tickSize));
  if (!profile) {
    emptyRange(scope, shape, [a]);
    return;
  }
  const left = scope.screen({ time: profile.firstTime, price: profile.low })?.x ?? null;
  if (left === null) return;
  anchorMark(scope, left, shape.color);
  // Профиль с привязкой живёт у края графика: справа — от правого края влево, слева — от привязки вправо.
  const right = Math.max(scope.width, left + 24);
  // Не шире четверти графика: привязка в глубокой истории иначе закрыла бы гистограммой все свечи.
  const reach = Math.max(48, Math.min(scope.width * 0.25, ((right - left) * options.width) / 100));
  const frame: ProfileFrame = {
    left,
    right,
    base: options.side === 'left' ? left : right,
    direction: options.side === 'left' ? 1 : -1,
    reach,
    levelsTo: right,
  };
  const drawn = drawProfile(scope, shape, profile, options, frame);
  if (!drawn) return;
  scope.anchors?.set(shape.id, [{ x: left, y: drawn.poc }]);
  hits.set(shape.id, {
    lines: [
      [{ x: left, y: drawn.poc }, { x: right, y: drawn.poc }],
      [{ x: left, y: drawn.top }, { x: left, y: drawn.bottom }],
    ],
    rects: [histogramRect(frame, drawn.top, drawn.bottom)],
    frame: null,
  });
}
